import { CATALOG, catalogItem } from './catalog.js';
import { chooseVariant, isVariant } from './variants.js';
export const KINDS = CATALOG.map(item=>item.kind);
const KEY = 'mofu-collection-v1';
export function chooseKind(random) {
  const value=Number.isFinite(random)?Math.max(0,Math.min(.999999,random)):0;
  return KINDS[Math.floor(value*KINDS.length)];
}
const validToy = t => t && typeof t.id === 'string' && t.id.length < 100 && KINDS.includes(t.kind) && Number.isFinite(t.createdAt);
const shellPieces = Array.from({ length: 40 }, (_, index) => index);
const cleanDamage = indices => [...new Set((Array.isArray(indices) ? indices : []).filter(index => Number.isInteger(index) && index >= 0 && index < 40))].sort((a, b) => a - b);
const cleanToy = t => {
  const waxDamage = catalogItem(t.kind)?.material === 'wax' ? cleanDamage(t.waxBroken === true ? shellPieces : t.waxDamage) : [];
  return { id: t.id, kind: t.kind, createdAt: t.createdAt, waxDamage, waxBroken: waxDamage.length === 40,
    ...(isVariant(t.kind, t.variant) ? { variant: t.variant } : {}) };
};
const settingChecks = {
  selectedId: value => value === null || typeof value === 'string',
  muted: value => typeof value === 'boolean',
  locale: value => ['en', 'ja'].includes(value),
  reducedMotion: value => typeof value === 'boolean',
  installHintDismissed: value => typeof value === 'boolean',
};
const settingsOf = state => Object.fromEntries(Object.keys(settingChecks).map(key => [key, state[key]]));

export class ToyStore {
  constructor(storage, options = {}) {
    this.storage = storage;
    this.random = options.random ?? Math.random;
    // Rare colours roll on their own draw so tests can pin the kind and the rarity apart.
    this.variantRandom = options.variantRandom ?? (options.random ? () => (this.random() * 9973 + .37) % 1 : Math.random);
    this.id = options.id ?? (() => crypto.randomUUID());
    this.now = options.now ?? Date.now;
    this.state = { screen: 'gacha', catalogFilter: 'all', phase: 'idle', collection: [], pending: null, selectedId: null,
      muted: true, locale: options.locale ?? 'en', mode: 'touch', reducedMotion: options.reducedMotion ?? false, installHintDismissed: false, canInstall: false, transferResult: null,
      status: null, newToy: false, previewKind: null, previewWaxBroken: false, previewWaxDamage: [], previewRevision: 0 };
    try {
      const data = JSON.parse(storage.getItem(KEY));
      if (data?.version === 1 && Array.isArray(data.collection)) {
        const unique = new Map(data.collection.filter(validToy).map(t => [t.id, cleanToy(t)]));
        this.state.collection = [...unique.values()];
        this.state.selectedId = unique.has(data.selectedId) ? data.selectedId : this.state.collection[0]?.id ?? null;
        if (validToy(data.pending) && !unique.has(data.pending.id)) {
          this.state.pending = cleanToy(data.pending);
          this.state.phase = 'sealed';
        }
        if (typeof data.muted === 'boolean') this.state.muted = data.muted;
        if (['en', 'ja'].includes(data.locale)) this.state.locale = data.locale;
        if (typeof data.reducedMotion === 'boolean') this.state.reducedMotion = data.reducedMotion;
        if (typeof data.installHintDismissed === 'boolean') this.state.installHintDismissed = data.installHintDismissed;
      }
    } catch { /* Corrupt or inaccessible storage starts a usable session. */ }
    this.savedSettings = settingsOf(this.state);
  }
  mergeStored() {
    let data;
    try { data = JSON.parse(this.storage.getItem(KEY)); }
    catch { return; }
    if (data?.version !== 1 || !Array.isArray(data.collection)) return;

    // Acquired toys and broken shells are permanent, including across tabs.
    const unique = new Map();
    for (const toy of [...data.collection, ...this.state.collection].filter(validToy)) {
      const previous = unique.get(toy.id);
      const current = cleanToy(toy);
      unique.set(toy.id, cleanToy({
        ...(previous ?? current),
        waxDamage: [...(previous?.waxDamage ?? []), ...current.waxDamage],
        waxBroken: previous?.waxBroken === true || current.waxBroken,
      }));
    }
    this.state.collection = [...unique.values()];
    const previousPending = this.state.pending;
    const pending = [data.pending, previousPending].find(toy => validToy(toy) && !unique.has(toy.id));
    this.state.pending = pending ? cleanToy(pending) : null;
    if (pending && pending.id !== previousPending?.id) this.state.phase = 'sealed';
    else if (!pending && previousPending && ['rolling', 'sealed'].includes(this.state.phase)) this.state.phase = 'idle';
    // Keep local edits, but do not write unchanged settings over a newer tab.
    for (const [key, valid] of Object.entries(settingChecks)) {
      if (!valid(data[key])) continue;
      if (this.state[key] === this.savedSettings[key]) this.state[key] = data[key];
      this.savedSettings[key] = data[key];
    }
    if (!unique.has(this.state.selectedId)) {
      this.state.selectedId = unique.has(data.selectedId) ? data.selectedId : this.state.collection[0]?.id ?? null;
    }
  }
  persist() {
    try {
      this.mergeStored();
      const { collection, pending, selectedId, muted, locale, reducedMotion, installHintDismissed } = this.state;
      this.storage.setItem(KEY, JSON.stringify({ version: 1, collection, pending, selectedId, muted, locale, reducedMotion, installHintDismissed }));
      this.savedSettings = settingsOf(this.state);
      if (this.state.status === 'save-error') this.state.status = null;
      return true;
    } catch { this.state.status = 'save-error'; return false; }
  }
  draw() {
    this.mergeStored();
    if (this.state.pending || this.state.phase === 'rolling') return false;
    const kind = chooseKind(this.random());
    this.state.pending = cleanToy({ id: this.id(), kind, createdAt: this.now(), variant: chooseVariant(kind, this.variantRandom()) });
    Object.assign(this.state, { screen: 'gacha', phase: 'rolling', newToy: false, previewKind: null });
    this.persist();
    return true;
  }
  seal() { if (this.state.phase === 'rolling') this.state.phase = 'sealed'; }
  open() {
    const requestedId = this.state.pending?.id;
    this.mergeStored();
    if (this.state.phase !== 'sealed' || !this.state.pending) return null;
    if (requestedId !== this.state.pending.id) return null;
    const toy = this.state.pending;
    this.state.collection.push(toy);
    Object.assign(this.state, { pending: null, selectedId: toy.id, phase: 'revealed', newToy: true, previewKind: null });
    this.persist();
    return toy;
  }
  select(id) {
    if (!this.state.collection.some(t => t.id === id)) return;
    Object.assign(this.state, { selectedId: id, screen: 'play', newToy: false, mode: 'touch', previewKind: null });
    this.persist();
  }
  crack(indices = shellPieces, displayedId = this.state.selectedId) {
    const added = cleanDamage(indices);
    if (this.state.previewKind) {
      if (catalogItem(this.state.previewKind)?.material === 'wax') {
        this.state.previewWaxDamage = cleanDamage([...this.state.previewWaxDamage, ...added]);
        this.state.previewWaxBroken = this.state.previewWaxDamage.length === 40;
      }
      return;
    }
    const toy = this.state.collection.find(t => t.id === displayedId);
    if (catalogItem(toy?.kind)?.material === 'wax' && !toy.waxBroken) {
      const waxDamage = cleanDamage([...toy.waxDamage, ...added]);
      if (waxDamage.length === toy.waxDamage.length) return;
      toy.waxDamage = waxDamage;
      toy.waxBroken = waxDamage.length === 40;
      this.persist();
    }
  }
  reset() {
    if(this.state.previewKind){this.state.previewWaxBroken=false;this.state.previewWaxDamage=[];return;}
    const toy = this.state.collection.find(t => t.id === this.state.selectedId);
    if (toy && catalogItem(toy.kind)?.material !== 'wax') { toy.waxBroken = false; this.persist(); }
  }
  // Toys from a transfer code join the collection by id. Shell damage unions
  // like a second tab; nothing already owned is replaced.
  importToys(toys) {
    this.mergeStored();
    const known = new Map(this.state.collection.map(toy => [toy.id, toy]));
    let added = 0, changed = false;
    for (const raw of Array.isArray(toys) ? toys : []) {
      if (!validToy(raw)) continue;
      const toy = cleanToy(raw), previous = known.get(toy.id);
      if (previous) {
        const waxDamage = cleanDamage([...previous.waxDamage, ...toy.waxDamage]);
        if (waxDamage.length !== previous.waxDamage.length) { previous.waxDamage = waxDamage; previous.waxBroken = waxDamage.length === 40; changed = true; }
        continue;
      }
      known.set(toy.id, toy); this.state.collection.push(toy); added++;
    }
    if (!this.state.selectedId && this.state.collection.length) this.state.selectedId = this.state.collection[0].id;
    if (added || changed) this.persist();
    return added;
  }
  preview(kind) {
    if(!catalogItem(kind))return;
    Object.assign(this.state,{previewKind:kind,previewWaxBroken:false,previewWaxDamage:[],previewRevision:this.state.previewRevision+1,screen:'play',mode:'touch',newToy:false});
  }
}

// A transfer code carries the collection to another browser or device without
// an account: MOFU1- is deflate-raw + base64url, MOFU0- is plain base64url for
// browsers without CompressionStream. Settings and pending capsules stay local.
const encodeBase64Url = bytes => {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
const decodeBase64Url = text => Uint8Array.from(atob(text.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(text.length / 4) * 4, '=')), c => c.charCodeAt(0));
const pipe = async (bytes, stream) => new Uint8Array(await new Response(new Blob([bytes]).stream().pipeThrough(stream)).arrayBuffer());
export async function encodeTransfer(collection) {
  const bytes = new TextEncoder().encode(JSON.stringify({ version: 1, collection: (Array.isArray(collection) ? collection : []).filter(validToy).map(cleanToy) }));
  if (typeof CompressionStream === 'function') {
    try { return `MOFU1-${encodeBase64Url(await pipe(bytes, new CompressionStream('deflate-raw')))}`; }
    catch { /* Fall through to the plain encoding. */ }
  }
  return `MOFU0-${encodeBase64Url(bytes)}`;
}
export async function decodeTransfer(code) {
  const match = String(code ?? '').replace(/\s+/g, '').match(/^MOFU([01])-([A-Za-z0-9_-]+)$/);
  if (!match) return null;
  try {
    let bytes = decodeBase64Url(match[2]);
    if (match[1] === '1') {
      if (typeof DecompressionStream !== 'function') return null;
      bytes = await pipe(bytes, new DecompressionStream('deflate-raw'));
    }
    const data = JSON.parse(new TextDecoder().decode(bytes));
    if (data?.version !== 1 || !Array.isArray(data.collection)) return null;
    return data.collection.filter(validToy).map(cleanToy);
  } catch { return null; }
}
